import { useSyncExternalStore } from "react";

// User's preferred distance unit for the search radius. Display-only — the
// API and durable storage always work in kilometres. The account is the
// source of truth (synced via /api/prefs); localStorage is an instant-start
// cache so the first render doesn't flash the default before the fetch.

export type DistanceUnit = "mi" | "km";

const KEY = "fish-finder:distance-unit";
const KM_PER_MI = 1.60934;

function load(): DistanceUnit {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "km" || v === "mi") return v;
  } catch {
    /* ignore — fall through to default */
  }
  return "mi"; // default to miles
}

let unit: DistanceUnit = load();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): DistanceUnit {
  return unit;
}

export function useDistanceUnit(): DistanceUnit {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

// Update the in-memory store + localStorage cache. Does not touch the server.
function apply(next: DistanceUnit): void {
  if (next === unit) return;
  unit = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* ignore — preference just won't persist locally */
  }
  listeners.forEach((l) => l());
}

// User-initiated change: update locally for an instant response, then
// persist to the account (best-effort — localStorage already holds it).
export function setDistanceUnit(next: DistanceUnit): void {
  apply(next);
  void fetch("/api/prefs", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ key: "distance_unit", value: next }),
  }).catch(() => {
    /* offline — the localStorage cache will resync on the next change */
  });
}

// Pull the account's saved preference and adopt it. Call once the user is
// signed in; the account value wins over the local default.
export async function loadDistanceUnit(): Promise<void> {
  try {
    const res = await fetch("/api/prefs", { credentials: "same-origin" });
    if (!res.ok) return;
    const body = (await res.json()) as { prefs?: Record<string, string> };
    const v = body.prefs?.distance_unit;
    if (v === "mi" || v === "km") apply(v);
  } catch {
    /* offline — keep the localStorage value */
  }
}

export function unitLabel(u: DistanceUnit): string {
  return u; // "mi" | "km" — already the conventional abbreviation
}

// A radius value entered in `u` -> kilometres, rounded + clamped to the
// range the API accepts (matches the worker's clampRadius).
export function toKm(value: number, u: DistanceUnit): number {
  const km = u === "mi" ? value * KM_PER_MI : value;
  return Math.min(100, Math.max(1, Math.round(km)));
}

// Kilometres -> a whole number in `u`, for display.
export function fromKm(km: number, u: DistanceUnit): number {
  return Math.round(u === "mi" ? km / KM_PER_MI : km);
}
